import { useState } from 'react';
import { Typography, Table, Button, Modal, Form, Input, Select, message, Space, Tag } from 'antd';
import { UserAddOutlined, CopyOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import MyLogins from '../components/MyLogins';

const ROLES = [
  { value: 'admin', label: '管理員 admin' },
  { value: 'editor', label: '主編 editor' },
  { value: 'author', label: '記者 author' },
  { value: 'viewer', label: '檢視者 viewer' },
];
const PROVIDER_COLOR: Record<string, string> = { line: 'green', google: 'blue' };

export default function UsersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [form] = Form.useForm<{ email: string; roleId: string }>();
  const users = useQuery({ queryKey: ['users-manage'], queryFn: api.usersManage });

  const setRole = useMutation({
    mutationFn: (v: { id: string; roleId: string }) => api.setUserRole(v.id, v.roleId),
    onSuccess: () => {
      message.success('角色已更新');
      qc.invalidateQueries({ queryKey: ['users-manage'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const invite = useMutation({
    mutationFn: (v: { email: string; roleId: string; send: boolean }) => api.invite(v.email, v.roleId, v.send),
    onSuccess: (d, v) => {
      setLink(d.acceptUrl);
      message.success(v.send ? (d.emailed ? '邀請信已寄出' : '已產生連結（寄信未啟用，請複製連結）') : '已產生邀請連結');
    },
    onError: (e: Error) => message.error(e.message),
  });
  const submit = (send: boolean) => form.validateFields().then((v) => invite.mutate({ ...v, send }));

  const columns = [
    { title: '使用者', dataIndex: 'displayName' },
    { title: 'Email', dataIndex: 'email', render: (e: string | null) => e || <span className="text-gray-300">—</span> },
    {
      title: '登入方式',
      dataIndex: 'providers',
      render: (p: string | null) =>
        p ? p.split(',').map((x) => <Tag key={x} color={PROVIDER_COLOR[x] ?? 'default'}>{x}</Tag>) : '—',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 180,
      render: (roles: string | null, r: { id: string }) => (
        <Select
          size="small"
          className="w-[150px]"
          value={roles?.split(',')[0]}
          placeholder="未指派"
          options={ROLES}
          onChange={(roleId) => setRole.mutate({ id: r.id, roleId })}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Typography.Title level={4} className="!mb-0">
          帳號管理
        </Typography.Title>
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => { setLink(null); setOpen(true); }}>
          邀請使用者
        </Button>
      </div>

      <MyLogins />

      <Table rowKey="id" loading={users.isLoading} dataSource={users.data ?? []} columns={columns} />

      <Modal title="邀請使用者" open={open} onCancel={() => setOpen(false)} footer={null}>
        <Form form={form} layout="vertical" className="mt-3">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: '請輸入有效 email' }]}>
            <Input placeholder="someone@example.com" />
          </Form.Item>
          <Form.Item name="roleId" label="角色" rules={[{ required: true, message: '請選擇角色' }]}>
            <Select options={ROLES} placeholder="選擇角色" />
          </Form.Item>
          <Space>
            <Button type="primary" loading={invite.isPending} onClick={() => submit(true)}>
              寄送邀請信
            </Button>
            <Button loading={invite.isPending} onClick={() => submit(false)}>
              產生邀請連結
            </Button>
          </Space>
        </Form>
        {link && (
          <div className="mt-4">
            <Typography.Text type="secondary" className="text-xs">
              邀請連結（7 天內有效）：
            </Typography.Text>
            <Input.Search
              className="mt-1"
              value={link}
              enterButton={<CopyOutlined />}
              onSearch={() => {
                navigator.clipboard.writeText(link);
                message.success('已複製');
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
